/**
 * Builds an editor document from an assessment in the simple shape the
 * built-in templates use: question sets of questions with their answers, and
 * categories in `config`.
 *
 * It is the way into the editor for anything that isn't already an editor
 * document: a built-in template being customized, a template shared from
 * another workspace (its published questions), and generated assessments.
 *
 * Question and question set keys are kept, so results of a customized
 * assessment still line up with earlier runs of the original.
 */

import { Doc as YDoc, Map as YMap } from 'yjs';
import { merge } from 'lodash';

import {
  CURRENT_DOC_FORMAT_VERSION,
  initializeYDoc,
} from 'js/components/Assessment/Editors/common/binding';
import { getDefaultAnswerSetTypeForQuestionType } from 'js/components/Assessment/Editors/common/components/AnswerSets/answer-set-utils';
import { positionify } from 'js/ydoc/docs/common/utils';
import { nextColorIndex } from 'js/utils/color';
import { randomBase32String } from 'js/utils/string';
import {
  DEFAULT_DECORATION,
  SENTIMENT_TYPE,
} from 'js/utils/sentiment-question';

const ASSESSMENT_PAGES_PARENT = 'assessmentTemplate';
const DEFAULT_QUESTION_TYPE = 'likert';

/**
 * The starting state of a new template's document. Shared with the template
 * creator so a converted document looks like one made from scratch.
 */
export function baseDocState(type, overrides = null) {
  return merge(
    {
      meta: {
        type,
        configurable: true,
        name: 'New Assessment',
        description:
          'Evaluate key skills and competencies to identify strengths and areas for growth in your team.',
        isPublic: false,
        doc_format_version: CURRENT_DOC_FORMAT_VERSION,
      },
      assessment: {},
      pages: [],
      answerSets: {},
      config: {
        version: '1.0',
        categories: {},
      },
      views: {},
    },
    overrides
  );
}

const newId = (prefix) => `${prefix}-${randomBase32String(10)}`;

function yMap(entries) {
  return new YMap(Object.entries(entries));
}

function position(parentType, parentId, p) {
  return yMap({ [parentType]: yMap({ t: parentId, p }) });
}

/**
 * Categories keep their keys, so a question set's `category` still points at
 * the right one. Each gets a color the way the editor assigns them.
 */
function buildCategories(questionSets, configCategories = {}) {
  const used = [...new Set(questionSets.map((qs) => qs.category))].filter(
    (key) => typeof key === 'string'
  );

  const colors = [];

  return Object.fromEntries(
    used
      .map((key, index) => ({
        key,
        display_name: configCategories[key]?.display_name ?? key,
        ordinal: configCategories[key]?.ordinal ?? index + 1,
      }))
      .sort((a, b) => a.ordinal - b.ordinal)
      .map(({ key, display_name }, index, all) => {
        const colorIndex = nextColorIndex(colors);
        colors.push(colorIndex);

        return [
          key,
          {
            display_name,
            ordinal: String((index + 1) / (all.length + 1)),
            colorIndex,
          },
        ];
      })
  );
}

/**
 * The editor keeps N/A as a flag on the answer set rather than as a choice.
 * Sentiment scales list their most negative option first, valued upward.
 */
function answerSetFields(question, type) {
  const answers = question.answers ?? [];
  const na = answers.find((answer) => answer.is_na);
  let choices = answers.filter((answer) => !answer.is_na);

  if (type === SENTIMENT_TYPE) {
    choices = [...choices].sort((a, b) => Number(a.value) - Number(b.value));
  }

  return {
    choices: positionify(
      choices.map(({ value, text }) => ({
        value: value == null ? null : String(value),
        text,
      }))
    ),
    includeNA: !!na,
    naText: na?.text ?? 'N/A',
  };
}

function choicesMap(choices) {
  return new YMap(
    choices.map((choice) => {
      const guid = newId('c');
      return [guid, yMap({ ...choice, guid })];
    })
  );
}

/**
 * Questions with the same answers share one answer set, the way an author
 * would set up a survey that uses one scale throughout. Sentiment questions
 * always get their own: their options are the substance of the question.
 */
function answerSetFor(doc, question, type, shared) {
  const answerSetType = getDefaultAnswerSetTypeForQuestionType(type);

  if (!answerSetType || !(question.answers?.length > 0)) {
    return null;
  }

  const fields = answerSetFields(question, type);
  const signature = JSON.stringify([answerSetType, fields]);

  if (type !== SENTIMENT_TYPE && shared.has(signature)) {
    return shared.get(signature);
  }

  const guid = newId('as');
  doc.getMap('answerSets').set(
    guid,
    yMap({
      guid,
      name: null,
      type: answerSetType,
      isStandard: false,
      isPublished: false,
      includeNA: fields.includeNA,
      naText: fields.naText,
      choices: choicesMap(fields.choices),
    })
  );

  shared.set(signature, guid);
  return guid;
}

function addQuestion(doc, question, pageGuid, p, shared) {
  const guid = randomBase32String(10);
  const type = question.type ?? DEFAULT_QUESTION_TYPE;
  const entries = {
    elementType: 'question',
    guid,
    key: question.key ?? `q-${guid}`,
    text: question.text,
    type,
    position: position('pageElements', pageGuid, p),
  };

  if (type === SENTIMENT_TYPE) {
    const decoration = question.config?.decoration ?? DEFAULT_DECORATION;
    entries.decoration = decoration;
    entries.config = yMap({ ...question.config, decoration });
  } else if (question.config) {
    entries.config = yMap(question.config);
  }

  const answerSetId = answerSetFor(doc, question, type, shared);
  if (answerSetId) {
    entries.answerSetId = answerSetId;
  }

  doc.getMap('elements').set(guid, yMap(entries));
}

/**
 * Fills `doc` from `assessment` ({ name, description, question_sets }) and
 * returns it. Pass a fresh document, or one a binding already observes.
 */
export function buildTemplateDoc(
  { type, assessment, config = null, name, description },
  doc = new YDoc()
) {
  const questionSets = assessment.question_sets ?? [];

  initializeYDoc(
    doc,
    baseDocState(type, {
      // lodash's merge skips undefined, keeping the default description
      meta: {
        name: name ?? assessment.name,
        description: description ?? assessment.description,
      },
      config: {
        categories: buildCategories(questionSets, config?.categories),
      },
    })
  );

  const shared = new Map();

  doc.transact(() => {
    positionify(questionSets).forEach((questionSet) => {
      const guid = randomBase32String(10);
      const page = {
        pageType: 'question-set',
        name: questionSet.name,
        guid,
        key: questionSet.key ?? `qs-${guid}`,
        position: position(
          'assessmentPages',
          ASSESSMENT_PAGES_PARENT,
          questionSet.p
        ),
      };

      if (typeof questionSet.category === 'string') {
        page.category = questionSet.category;
      }

      doc.getMap('pages').set(guid, yMap(page));

      positionify(questionSet.questions ?? []).forEach((question) =>
        addQuestion(doc, question, guid, question.p, shared)
      );
    });
  });

  return doc;
}
